import React from 'react';
import _uniq from 'lodash/uniq';
import { RangeSlider } from 'ui/rangeSlider/RangeSlider';
import { FormField } from 'ui/formField';
import { TextArea } from 'ui/inputs/textArea';
import noop from 'utils/noop';
import { arrayToObject } from 'utils/helpers';
import { MultiSelect } from 'ui/selectv2/MultiSelect';
import { Toggle, TOGGLE_SIZES } from 'ui/toggle';
import { Divider } from 'ui/divider';
import {
  PrimaryButton,
  GhostSuccessButton,
  GhostErrorButton,
  SecondaryButton,
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from 'ui/button';
import { Edit16 } from 'ui/icons';
import { Modal, ModalFooter, ModalBody, ModalHeader, MODAL_SIZES, MODAL_VARIANTS } from 'ui/modal';
import { FractionalInputList, NonFractionalInputList } from './inputLists';
import { FRACTIONAL_INPUT_LISTS, INPUT_LISTS } from './constants';
import { ConfirmationModalBody } from './ConfirmationModalBody';
import {
  DescriptionContentContainer,
  DescriptionText,
  GlobalToggleContainer,
  InputListsContainer,
  OrgContainer,
  SliderContainer,
  StyledIconButton,
} from './EditModal.styled';

export const PRODUCTION = 'PRODUCTION';

// type Props = {
//   /** List of org admins with org id */
//   adminUsers: object[],
//   /** If the feature flag is enabled globally. */
//   enabledGlobally: boolean,
//   /** List of org IDs enabled. */
//   enabledOrgIds: [],
//   /** List of project IDs enabled. */
//   enabledProjectIds: [],
//   /** Current environment ie. PRODUCTION-US */
//   env: String,
//   /** List of org IDs disabled. */
//   disabledOrgIds: [],
//   /** List of project IDs disabled. */
//   disabledProjectIds: [],
//   /** Currently selected featureFlag to edit. */
//   featureFlag: {},
//   /** The global fraction value of pf the feature flag if `isFractional`. */
//   globalFraction: number,
//   /** Header text of the modal. */
//   header: string,
//   /** If the feature flag is of fractional values. */
//   isFractional: boolean,
//   /** Callback function when modal is closed. */
//   onClose: () => void,
//   /** Callback function when saving. */
//   onSave: () => void,
//   /** Array of orgIdFrations containing orgId value and fraction percentageValue. */
//   orgIdFractions: [],
//   /** Array of projectIdFractions containing projectId value and fraction percentageValue. */
//   projectIdFractions: [],
// };
export default class EditModal extends React.Component {
  static defaultProps = {
    disabledOrgIds: [],
    disabledProjectIds: [],
    enabledOrgIds: [],
    enabledProjectIds: [],
    isFractional: false,
    onClose: noop,
    onSave: noop,
  };

  constructor(props) {
    super(props);

    this.state = {
      comment: `Changing feature flag in ${
        this.props.env.includes(PRODUCTION) ? 'production' : 'non-production'
      } environment`,
      confirmation: '',
      disabledOrgIds: props.disabledOrgIds || [],
      disabledProjectIds: props.disabledProjectIds || [],
      editNotesMode: false,
      enabledGlobally: props.enabledGlobally || false,
      enabledOrgIds: props.enabledOrgIds || [],
      enabledProjectIds: props.enabledProjectIds || [],
      env: props.env || '',
      globalFraction: props.globalFraction || 0,
      isConfirmationMode: false,
      notes: props.featureFlag?.notes || '',
      orgDropdownValue: [],
      orgIdFractions: props.orgIdFractions || [],
      projectIdFractions: props.projectIdFractions || [],
    };
  }

  /**
   * handleChangeEditNotesMode
   * Callback function to toggle notes(feature description) edit mode between true/false.
   */
  handleChangeEditNotesMode = () => {
    this.setState({ editNotesMode: !this.state.editNotesMode });
  };

  /**
   * handleChangeNotes
   * Callback function when note is changed.
   */
  handleChangeNotes = (value) => {
    this.setState({ notes: value });
  };

  /**
   * handleChangeComment
   * Callback function when comment is changed.
   */
  handleChangeComment = (value) => {
    this.setState({ comment: value });
  };

  /**
   * handleChangeConfirmation
   * Callback function when the confirmation text is changed.
   * @param { Object } e: The change event.
   * @param { Object } inputProps: Updated props of confirmation input text area.
   * @returns { void }
   */
  handleChangeConfirmation = (value) => {
    this.setState({ confirmation: value });
  };

  /**
   * handleChangeGlobalFraction
   * Callback function when global fraction value is changed.
   */
  handleChangeGlobalFraction = (value) => {
    const fractionNumber = parseInt(value || 0, 10);
    if (fractionNumber >= 0 && fractionNumber <= 100) {
      this.setState({ globalFraction: value / 100 });
    }
  };

  /**
   * getGlobalFractionValue
   * Gets the global fraction value as a percentage.
   * @returns { Number } The global fraction value.
   */
  getGlobalFractionValue = () => {
    const { globalFraction } = this.state;
    const fractionValue = globalFraction === undefined ? 1 : globalFraction;
    return Math.round(fractionValue * 100);
  };

  /**
   * formatFractionsForSave
   * Formats the fraction value arrays to objects for save.
   * @param { Array } fractions: The fractions to format for save.
   * @returns { Object } The formatted fractions
   */
  formatFractionsForSave = (fractions) => {
    const filteredFractions = fractions.filter(({ value }) => value !== '');
    return arrayToObject(filteredFractions, 'value', 'percentageValue');
  };

  /**
   * handleBausDropdownChange
   * Updates the org dropdown values on update.
   * @param { Array } orgDropdownValue: The current selected orgs.
   * @returns { void }
   */
  handleBausDropdownChange = (orgDropdownValue) =>
    this.setState({
      orgDropdownValue,
    });

  /**
   * transferOrgDropdownValues
   * Transfers values from the org dropdown to the designated row.
   * @param { String } key: The current selected orgs.
   * @returns { function } An onclick function
   */
  transferOrgDropdownValues = (key) => () => {
    const selectedOrgIds = this.state.orgDropdownValue.map((org) => org.value.orgId);
    // array of org ids
    this.setState({
      [key]: _uniq([...this.state[key], ...selectedOrgIds]),
      orgDropdownValue: [],
    });
  };

  /**
   * handleSave
   * Callback function when save button is clicked.
   * @param { Object } e: The click event.
   * @returns { void }
   */
  handleSave = (e) => {
    const { featureFlag, isFractional, onClose, onSave } = this.props;
    const {
      comment,
      disabledOrgIds,
      disabledProjectIds,
      enabledGlobally,
      enabledOrgIds,
      enabledProjectIds,
      globalFraction,
      notes,
      orgIdFractions,
      projectIdFractions,
    } = this.state;

    const removeWhitespace = (array) => array.filter((element) => element !== '');

    const settings = isFractional
      ? {
          globalFraction,
          orgIdFractions: this.formatFractionsForSave(orgIdFractions),
          projectIdFractions: this.formatFractionsForSave(projectIdFractions),
        }
      : {
          enabledGlobally,
          enabledOrgIds: removeWhitespace(enabledOrgIds),
          enabledProjectIds: removeWhitespace(enabledProjectIds),
          disabledOrgIds: removeWhitespace(disabledOrgIds),
          disabledProjectIds: removeWhitespace(disabledProjectIds),
        };

    onSave({ ...featureFlag, notes, settings }, comment);
    onClose();
  };

  render() {
    const { featureFlag, header, isFractional, onClose, adminUsers } = this.props;
    const { comment, confirmation, editNotesMode, enabledGlobally, orgDropdownValue, notes } =
      this.state;
    const inputListCategories = isFractional ? FRACTIONAL_INPUT_LISTS : INPUT_LISTS;

    return this.state.isConfirmationMode ? (
      <>
        <Modal
          id="confirmation-featureFlag-modal"
          isModalOpen={true}
          onClose={onClose}
          size={MODAL_SIZES.MEDIUM}
          variant={MODAL_VARIANTS.DEFAULT}
        >
          <ModalHeader>{header}</ModalHeader>
          <ModalBody>
            <ConfirmationModalBody
              confirmation={this.state.confirmation}
              currentState={this.props}
              env={this.props.env}
              flagName={header}
              handleChangeConfirmation={this.handleChangeConfirmation}
              updatedState={this.state}
            />
          </ModalBody>
          <ModalFooter hasCancel={false}>
            <SecondaryButton
              onClick={() => this.setState({ isConfirmationMode: false, confirmation: '' })}
              size={BUTTON_SIZES.LARGE}
            >
              Edit changes
            </SecondaryButton>
            <PrimaryButton
              isDisabled={confirmation !== this.props.env}
              id="saveButton"
              onClick={this.handleSave}
              size={BUTTON_SIZES.LARGE}
            >
              Save
            </PrimaryButton>
          </ModalFooter>
        </Modal>
      </>
    ) : (
      <Modal id="edit-featureFlag-modal" isOpen={true} onClose={onClose} size={MODAL_SIZES.LARGE}>
        <ModalHeader>{header}</ModalHeader>
        <ModalBody>
          {!editNotesMode ? (
            <DescriptionContentContainer>
              <DescriptionText>
                {notes.length ? notes : `Description for ${header} not provided yet...`}
              </DescriptionText>
              <StyledIconButton
                data-test="description-edit-button"
                onClick={() => this.handleChangeEditNotesMode()}
                variant={BUTTON_VARIANTS.GHOST}
              >
                <Edit16 />
              </StyledIconButton>
            </DescriptionContentContainer>
          ) : (
            <FormField
              inputId="featureFlagDescription"
              labelText={!featureFlag?.notes ? 'Add a description' : 'Edit the description'}
            >
              <TextArea
                autoFocus
                id="featureFlagDescription"
                onBlur={this.handleChangeEditNotesMode}
                onChange={this.handleChangeNotes}
                placeholder="(Engineering or Product Only) Add a description about this feature flag. It will be displayed in the table to provide context for what the flag does."
                value={notes}
              />
            </FormField>
          )}
          <Divider />
          {!isFractional ? (
            <GlobalToggleContainer>
              <Toggle
                isActive={enabledGlobally}
                onClick={() => this.setState({ enabledGlobally: !enabledGlobally })}
                size={TOGGLE_SIZES.LARGE}
              />
              <label>Enable globally</label>
            </GlobalToggleContainer>
          ) : (
            <SliderContainer>
              <FormField
                inputId="globalFractionSlider"
                labelText="Percent enabled"
                isHorizontalLayout
                descriptionText="The default percentage enabled globally by this feature flag."
              >
                <RangeSlider
                  data-test="global-fraction-slider"
                  value={[this.getGlobalFractionValue()]}
                  onValueChange={(value) => this.handleChangeGlobalFraction(value)}
                />
              </FormField>
            </SliderContainer>
          )}
          <Divider />
          {!isFractional && (
            <>
              <OrgContainer>
                <MultiSelect
                  inputId="orgSelect"
                  onSelect={this.handleBausDropdownChange}
                  options={adminUsers}
                  placeholder="Search or select orgs..."
                  value={orgDropdownValue}
                />
                <GhostSuccessButton
                  isDisabled={!!enabledGlobally}
                  onClick={this.transferOrgDropdownValues('enabledOrgIds')}
                >
                  Add to enabled orgs
                </GhostSuccessButton>
                <GhostErrorButton onClick={this.transferOrgDropdownValues('disabledOrgIds')}>
                  Add to disabled orgs
                </GhostErrorButton>
              </OrgContainer>
              <Divider />
            </>
          )}
          <InputListsContainer
            columns={isFractional ? 2 : 4}
            data-test="edit-modal-input-list-container"
          >
            {inputListCategories.map((inputList, index) => {
              return isFractional ? (
                <FractionalInputList
                  key={`input-${index}`}
                  inputList={inputList}
                  fractionsList={this.state[inputList.value]}
                  handleChange={({ name, fractionsList }) =>
                    this.setState({ [name]: fractionsList })
                  }
                />
              ) : (
                <NonFractionalInputList
                  isEnabledGlobally={!!enabledGlobally}
                  key={`input-${index}`}
                  inputList={inputList}
                  values={this.state[inputList.value]}
                  handleChange={({ name, values }) => this.setState({ [name]: values })}
                />
              );
            })}
          </InputListsContainer>
          <Divider />
          <FormField inputId="changeCommentTextArea" labelText="Change comment">
            <TextArea
              onChange={this.handleChangeComment}
              value={comment}
              id="changeCommentTextArea"
              placeholder="Add a comment about this change (Required)"
            />
          </FormField>
        </ModalBody>
        <ModalFooter>
          <SecondaryButton
            id="reviewButton"
            data-test="review-button"
            onClick={() => this.setState({ isConfirmationMode: true })}
            size={BUTTON_SIZES.LARGE}
          >
            Review changes
          </SecondaryButton>
        </ModalFooter>
      </Modal>
    );
  }
}
